import type { LangChainMessage } from "@assistant-ui/react-langgraph";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";

import {
  attachQuestionResponses,
  buildQuestionResponseSet,
  emptyQuestionAnswer,
  isQuestionAnswered,
  type QuestionDraftAnswer,
  type ThreadQuestionSet
} from "@/agents-v2/questions/question-responses";

type QuestionDraft = {
  answers: QuestionDraftAnswer[];
  phase: "draft" | "submitted";
  questionSet: ThreadQuestionSet;
  threadId: string;
  toolCallId: string;
};

type QuestionResponseContextValue = {
  activeToolCallIds: ReadonlyMap<string, ReadonlySet<string>>;
  drafts: ReadonlyMap<string, QuestionDraft>;
  prepareMessages: (
    threadId: string,
    messages: LangChainMessage[]
  ) => LangChainMessage[];
  setActiveToolCallIds: (threadId: string, toolCallIds: string[]) => void;
  submitDraft: (threadId: string, toolCallId: string) => void;
  updateAnswer: (
    threadId: string,
    toolCallId: string,
    questionSet: ThreadQuestionSet,
    questionIndex: number,
    answer: QuestionDraftAnswer
  ) => void;
};

const QuestionResponseContext =
  createContext<QuestionResponseContextValue | null>(null);

const draftKey = (threadId: string, toolCallId: string) =>
  `${threadId}\u0000${toolCallId}`;

export const useQuestionResponseController =
  (): QuestionResponseContextValue => {
    const draftsRef = useRef<Map<string, QuestionDraft>>(new Map());
    const activeIdsRef = useRef<Map<string, Set<string>>>(new Map());
    const [drafts, setDrafts] = useState<ReadonlyMap<string, QuestionDraft>>(
      draftsRef.current
    );
    const [activeToolCallIds, setActiveToolCallIdsState] = useState<
      ReadonlyMap<string, ReadonlySet<string>>
    >(activeIdsRef.current);
    const publishDrafts = useCallback(() => {
      setDrafts(new Map(draftsRef.current));
    }, []);

    const updateAnswer = useCallback(
      (
        threadId: string,
        toolCallId: string,
        questionSet: ThreadQuestionSet,
        questionIndex: number,
        answer: QuestionDraftAnswer
      ) => {
        const key = draftKey(threadId, toolCallId);
        const current = draftsRef.current.get(key);
        const answers = questionSet.questions.map((_, index) =>
          index === questionIndex
            ? answer
            : (current?.answers[index] ?? emptyQuestionAnswer())
        );
        draftsRef.current.set(key, {
          answers,
          phase: "draft",
          questionSet,
          threadId,
          toolCallId
        });
        publishDrafts();
      },
      [publishDrafts]
    );

    /**
     * Mark a draft as sent, so it never rides on a later message as well.
     *
     * A card answered through an interrupt hands its answers back as the
     * tool's result; without this the same draft would also be attached to
     * the next thing the user types and the agent would see it twice.
     */
    const submitDraft = useCallback(
      (threadId: string, toolCallId: string) => {
        const key = draftKey(threadId, toolCallId);
        const draft = draftsRef.current.get(key);
        if (!draft || draft.phase === "submitted") return;
        draftsRef.current.set(key, { ...draft, phase: "submitted" });
        publishDrafts();
      },
      [publishDrafts]
    );

    const setActiveToolCallIds = useCallback(
      (threadId: string, toolCallIds: string[]) => {
        const current = activeIdsRef.current.get(threadId);
        if (
          current?.size === toolCallIds.length &&
          toolCallIds.every((toolCallId) => current.has(toolCallId))
        ) {
          return;
        }
        activeIdsRef.current.set(threadId, new Set(toolCallIds));
        setActiveToolCallIdsState(new Map(activeIdsRef.current));
      },
      []
    );

    const prepareMessages = useCallback(
      (threadId: string, messages: LangChainMessage[]) => {
        // Nothing to attach to: a resume command (an interrupt answered by a
        // card, a navigation) sends no human message. Consuming the drafts
        // here would mark them sent and drop the answers on the floor.
        if (!messages.some((message) => message.type === "human")) {
          return messages;
        }
        const activeIds =
          activeIdsRef.current.get(threadId) ?? new Set<string>();
        const preparedResponses = [...draftsRef.current.values()].flatMap(
          (draft) => {
            if (
              draft.threadId !== threadId ||
              draft.phase !== "draft" ||
              !activeIds.has(draft.toolCallId)
            ) {
              return [];
            }
            const response = buildQuestionResponseSet(
              draft.toolCallId,
              draft.questionSet,
              draft.answers
            );
            if (!response) return [];
            draftsRef.current.set(draftKey(threadId, draft.toolCallId), {
              ...draft,
              phase: "submitted"
            });
            return [response];
          }
        );

        if (preparedResponses.length === 0) return messages;
        publishDrafts();
        return attachQuestionResponses(messages, preparedResponses);
      },
      [publishDrafts]
    );

    return useMemo(
      () => ({
        activeToolCallIds,
        drafts,
        prepareMessages,
        setActiveToolCallIds,
        submitDraft,
        updateAnswer
      }),
      [
        activeToolCallIds,
        drafts,
        prepareMessages,
        setActiveToolCallIds,
        submitDraft,
        updateAnswer
      ]
    );
  };

export function QuestionResponseProvider({
  children,
  value
}: {
  children: ReactNode;
  value: QuestionResponseContextValue;
}) {
  return (
    <QuestionResponseContext.Provider value={value}>
      {children}
    </QuestionResponseContext.Provider>
  );
}

export const useQuestionResponses = () => {
  const context = useContext(QuestionResponseContext);
  if (!context) {
    throw new Error(
      "useQuestionResponses must be used inside QuestionResponseProvider."
    );
  }
  return context;
};

export const getQuestionDraft = (
  drafts: ReadonlyMap<string, QuestionDraft>,
  threadId: string,
  toolCallId: string
) => drafts.get(draftKey(threadId, toolCallId));

export const countAnsweredQuestions = (draft: QuestionDraft | undefined) =>
  draft?.questionSet.questions.filter((question, index) =>
    isQuestionAnswered(question, draft.answers[index])
  ).length ?? 0;
